import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useSessionStore, DEFAULT_DENSITY, type DisplayDensity } from '../stores/session'

// Mirror the signed-in user's display_density preference onto the
// `<html>` element as a `data-density` attribute. style.css carries
// `html[data-density="dense"] …` rules that tighten line-height and
// vertical padding without per-component prop threading.
//
// Two valid values:
//   - "comfortable" (default) — current spacing
//   - "dense" — tighter padding/line-height for power users skimming
//
// Mounted from App.vue once so it's active for the whole SPA lifetime.
// No return value — pure side effect on `<html>`.
//
// Idempotent on null `currentUser` (signed-out state): clears the
// attribute so the sign-in form gets the comfortable defaults even if
// a previously-signed-in user had dense mode set.
export function useDisplayDensity(): void {
  const session = useSessionStore()
  const { currentUser } = storeToRefs(session)

  const density = computed<DisplayDensity>(() => currentUser.value?.display_density ?? DEFAULT_DENSITY)

  watch(density, applyDensity, { immediate: true })
}

function applyDensity(value: DisplayDensity): void {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('data-density', value)
}

// Toggle helper — exported so the command palette + a header menu
// entry can share one call site. Persists optimistically via the
// session store; failures roll back automatically.
export async function toggleDisplayDensity(): Promise<void> {
  const session = useSessionStore()
  const current = session.currentUser?.display_density ?? DEFAULT_DENSITY
  const next: DisplayDensity = current === 'dense' ? 'comfortable' : 'dense'
  await session.updatePreferences({ display_density: next })
}
